/**
 * Journey trail — records the path the user actually navigated, so
 * breadcrumbs, the sidebar highlight, and back-links can reflect *how* the
 * user got to the current page instead of only the URL's canonical ancestry.
 *
 * Model: a small stack of `{path, hash}` entries.
 *   - Navigating to a top-level section (sidebar, palette, list links)
 *     RESETS the trail to that section — a new journey root.
 *   - Navigating to a detail page PUSHES an entry.
 *   - Navigating to a path already in the trail TRUNCATES back to it
 *     (breadcrumb clicks, back-links, browser Back).
 *   - Same-path navigation (tab/hash switches) updates the last entry.
 *
 * Example: Dashboard → campaign row → keyword artifact gives
 *   [{/}, {/campaigns/<id>}, {/keywords/<id>}]
 * which the breadcrumb renders as
 *   Dashboard → <campaign goal> → <keyword seed>.
 *
 * The single writer is the router subscription in `RootShell`
 * (`routes/index.tsx`) — components never write the trail directly, so any
 * navigation mechanism (links, palette, redirects, Back/Forward) is
 * recorded uniformly. Deep links / refreshes seed a minimal
 * [section, detail] trail; the breadcrumb treats that case as "no journey"
 * and falls back to the canonical project-aware derivation.
 *
 * Persisted to sessionStorage so a tab refresh keeps the journey. Cleared
 * on sign-out (see `lib/auth.ts`).
 */
import { create } from 'zustand';
import { isSectionRootPath, matchDetail } from '@/lib/nav-registry';

const STORAGE_KEY = 'seo:nav-trail';
/** Root + up to 7 hops — deep enough for any real journey, bounded. */
const MAX_TRAIL = 8;

export interface TrailEntry {
  path: string;
  hash?: string;
}

const isAuthPath = (path: string): boolean =>
  path.startsWith('/login') || path.startsWith('/signup');

const readInitial = (): TrailEntry[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const entries = parsed.filter(
      (e): e is TrailEntry =>
        !!e && typeof e === 'object' && typeof (e as TrailEntry).path === 'string',
    );
    return entries.slice(0, MAX_TRAIL).map((e) => ({
      path: e.path,
      ...(typeof e.hash === 'string' && e.hash ? { hash: e.hash } : {}),
    }));
  } catch {
    return [];
  }
};

const persist = (trail: TrailEntry[]): void => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trail));
  } catch {
    /* ignore quota/privacy errors */
  }
};

function nextTrail(trail: TrailEntry[], path: string, hash: string): TrailEntry[] {
  const entry: TrailEntry = { path, ...(hash ? { hash } : {}) };

  // Same page (tab/hash/search change) — refresh the tail entry in place.
  const last = trail[trail.length - 1];
  if (last && last.path === path) {
    return [...trail.slice(0, -1), entry];
  }

  // Went back up to an ancestor already in the trail — truncate to it.
  const existing = trail.findIndex((e) => e.path === path);
  if (existing !== -1) {
    return [...trail.slice(0, existing), entry];
  }

  // A top-level section is a new journey root.
  if (isSectionRootPath(path)) {
    return [entry];
  }

  // Deep link / refresh onto a detail page with no journey yet: seed the
  // canonical parent so the sidebar has a root and back-links have a target.
  if (trail.length === 0) {
    const detail = matchDetail(path);
    return detail ? [{ path: detail.sectionPath }, entry] : [entry];
  }

  // Otherwise this is a hop deeper into the journey.
  const grown = [...trail, entry];
  if (grown.length <= MAX_TRAIL) return grown;
  // Keep the root, drop the oldest intermediate hop.
  return [grown[0]!, ...grown.slice(2)];
}

interface NavTrailState {
  trail: TrailEntry[];
  /** Record a completed navigation. Called only by the RootShell subscription. */
  record: (path: string, hash: string) => void;
  /** Forget the journey (sign-out, tenant switch). */
  clear: () => void;
}

export const useNavTrail = create<NavTrailState>((set, get) => ({
  trail: readInitial(),
  record: (path, hash) => {
    if (isAuthPath(path)) return;
    const trail = nextTrail(get().trail, path, hash);
    persist(trail);
    set({ trail });
  },
  clear: () => {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    set({ trail: [] });
  },
}));

/** Clear the journey outside React (auth store sign-out). */
export function clearNavTrail(): void {
  useNavTrail.getState().clear();
}

/**
 * The journey's root path — what the sidebar highlights. Falls back to null
 * (URL-based highlighting) when no journey exists yet.
 */
export function trailRoot(trail: TrailEntry[]): string | null {
  return trail[0]?.path ?? null;
}
